import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScanSession } from '@/features/scanner/ScanSessionContext';
import { analyzeBill, AnalyzeBillError } from '@/services/gemini/analyzeBill';

/**
 * Gemini performs extraction as a single request — there is no real
 * multi-step "reading bill / finding amount / ..." progress to report, so
 * this screen shows one honest state rather than simulating fake steps.
 */
export default function ProcessingPage() {
  const navigate = useNavigate();
  const { prepared, setExtractionResult } = useScanSession();
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    if (!prepared) return;
    setError(null);
    setRunning(true);
    try {
      const result = await analyzeBill(prepared.forAnalysis);
      setExtractionResult(result.billData, result.warnings);
      navigate('/review', { replace: true });
    } catch (err) {
      setError(err instanceof AnalyzeBillError ? err.message : "We couldn't analyze this bill. Please try again.");
    } finally {
      setRunning(false);
    }
  }, [prepared, setExtractionResult, navigate]);

  useEffect(() => {
    if (!prepared) {
      navigate('/scan', { replace: true });
      return;
    }
    void run();
    // Intentionally runs once per mount — retry is an explicit user action, not an effect dependency change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepared]);

  if (!prepared) return null;

  return (
    <div className="flex min-h-[80dvh] flex-col items-center justify-center gap-6 p-6 text-center">
      {error ? (
        <>
          <p role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
            {error}
          </p>
          <p className="text-xs text-slate-500">Your photo has been kept — you can try again or go back and retake it.</p>
          <div className="flex w-full flex-col gap-3">
            <button
              type="button"
              onClick={() => void run()}
              disabled={running}
              className="rounded-2xl bg-blue-700 px-6 py-4 text-base font-semibold text-white active:bg-blue-800 disabled:opacity-60"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => navigate('/scan/preview')}
              className="rounded-2xl border border-slate-300 bg-white px-6 py-4 text-base font-medium text-slate-700 active:bg-slate-100"
            >
              Back to Photo
            </button>
          </div>
        </>
      ) : (
        <>
          <div
            className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-700"
            role="status"
            aria-label="Analyzing your bill"
          />
          <p className="text-lg font-medium text-slate-800">Analyzing your bill…</p>
          <p className="text-sm text-slate-500">This usually takes a few seconds.</p>
        </>
      )}
    </div>
  );
}
